import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";

function stableSerialize(value) {
  if (value === null || value === undefined) return String(value);
  if (typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableSerialize).join(",")} ]`;
  const keys = Object.keys(value).sort();
  return `{${keys
    .map((k) => `${JSON.stringify(k)}:${stableSerialize(value[k])}`)
    .join(",")}}`;
}

function stableQueryKey(key) {
  // React Query supports array keys; object segments can be hard to reason about.
  // We convert any object segment into a stable string so changes reliably refetch.
  if (!Array.isArray(key)) return key;
  return key.map((segment) =>
    segment && typeof segment === "object" ? stableSerialize(segment) : segment,
  );
}

function normalizePaginatedResponse(payload) {
  // Backend pagination shape: { count, page, page_size, total_pages, next, previous, data }
  if (payload && typeof payload === "object" && Array.isArray(payload.data)) {
    return {
      list: payload.data,
      pagination: {
        count: payload.count,
        page: payload.page,
        page_size: payload.page_size,
        total_pages: payload.total_pages,
        next: payload.next,
        previous: payload.previous,
      },
    };
  }

  return {
    list: Array.isArray(payload) ? payload : payload ? [payload] : [],
    pagination: null,
  };
}

/**
 * @function usePaginatedQueryWithReduxSync
 * @description React Query powered paginated fetch that ALSO syncs the results into redux for backward compatibility.
 */
export function usePaginatedQueryWithReduxSync({
  queryKey,
  queryFn,
  enabled = true,
  reduxListActionType,
  reduxPaginationActionType,
  emptyList = [],
}) {
  const dispatch = useDispatch();

  const query = useQuery({
    queryKey: stableQueryKey(queryKey),
    queryFn,
    enabled,
    placeholderData: keepPreviousData,
    retry: 0,
  });

  const normalized = useMemo(
    () => normalizePaginatedResponse(query.data),
    [query.data],
  );

  useEffect(() => {
    if (!query.isSuccess) return;

    if (reduxListActionType) {
      dispatch({
        type: reduxListActionType,
        payload: normalized.list,
      });
    }

    if (reduxPaginationActionType && normalized.pagination) {
      dispatch({
        type: reduxPaginationActionType,
        payload: normalized.pagination,
      });
    }
  }, [
    dispatch,
    query.isSuccess,
    reduxListActionType,
    reduxPaginationActionType,
    normalized.list,
    normalized.pagination,
  ]);

  return useMemo(
    () => ({
      data: {
        data: normalized.list ?? emptyList,
        count: normalized.pagination?.count,
        page: normalized.pagination?.page,
        page_size: normalized.pagination?.page_size,
        total_pages: normalized.pagination?.total_pages,
        next: normalized.pagination?.next,
        previous: normalized.pagination?.previous,
      },
      loading: query.isLoading || query.isFetching,
      error: query.error,
      refetch: query.refetch,
    }),
    [
      normalized.list,
      normalized.pagination,
      emptyList,
      query.isLoading,
      query.isFetching,
      query.error,
      query.refetch,
    ],
  );
}

export default usePaginatedQueryWithReduxSync;
